import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';
import { isNativePlatform } from '../lib/platform';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Don't show install banner inside native app
    if (isNativePlatform()) return;

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    
    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Detect Android
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOSDevice || isAndroid) {
      // Show banner after 3 seconds on mobile
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    // Android: redirect to Google Play Store
    window.open('https://play.google.com/store/apps/details?id=com.tenminvestor.app', '_blank');
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 safe-area-bottom" data-testid="pwa-install-banner">
        <div className="max-w-lg mx-auto bg-slate-900 rounded-2xl p-4 shadow-2xl shadow-slate-900/30 flex items-center gap-3">
          <img src="/icons/icon-96x96.png" alt="10M Investor" className="w-12 h-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">10 Millions Investor</p>
            <p className="text-slate-400 text-xs">Installez l'app pour un accès rapide</p>
          </div>
          <button
            onClick={handleInstall}
            className="bg-[#00B050] text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5 flex-shrink-0 hover:bg-[#00B050]/90 transition-colors"
            data-testid="pwa-install-btn"
          >
            {isIOS ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            Installer
          </button>
          <button onClick={handleDismiss} className="text-slate-500 hover:text-white p-1 flex-shrink-0" data-testid="pwa-dismiss-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[101] bg-black/60 flex items-end justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 mb-4" data-testid="pwa-ios-guide">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900">Installer sur iPhone / iPad</h3>
              <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#0D47A1] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                <p className="text-sm text-slate-600 pt-0.5">Appuyez sur le bouton <strong>Partager</strong> <Share className="w-4 h-4 inline text-[#0D47A1]" /> en bas de Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#0D47A1] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                <p className="text-sm text-slate-600 pt-0.5">Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#00B050] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                <p className="text-sm text-slate-600 pt-0.5">Appuyez sur <strong>"Ajouter"</strong> et c'est fait !</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
